import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useEffect, useState, useCallback } from 'react';
import { WizardLayout } from '@/components/WizardLayout';
import { BuyFlowStep1 } from './BuyFlowStep1';
import { BuyFlowStep2 } from './BuyFlowStep2';
import { BuyFlowStep3Content } from './BuyFlowStep3Content';

/**
 * BuyWizard - Buy Flow wizard wrapper component
 * 
 * Handles:
 * - Route-based step navigation: `/buy/:productId/step-1` through `/buy/:productId/step-4`
 * - Step validation and progression logic
 * - Wizard state management across steps
 * - Browser back/forward navigation
 * 
 * State Management:
 * - Wizard state stored in component state (per Architecture section 6.3)
 * - State persists during browser navigation
 * - State cleared on Cancel or completion
 */
export const BuyWizard = () => {
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  // Extract current step from route path
  const getCurrentStep = (): number => {
    const match = location.pathname.match(/step-(\d+)/);
    if (match) {
      return parseInt(match[1], 10);
    }
    return 1; // Default to step 1
  };

  const currentStep = getCurrentStep();
  const totalSteps = 4;

  // Wizard state management
  const [wizardState, setWizardState] = useState<{
    step1Data?: any; // Document approvals
    step2Data?: any; // Terms review
    step3Data?: any; // 2FA verification
    step4Data?: any; // Confirmation
  }>({});

  // Step validation functions
  const validateStep = (step: number): boolean => {
    switch (step) {
      case 1:
        // Step 1 validation: all documents must be approved
        // This will be implemented in BuyFlowStep1 component
        return wizardState.step1Data?.allDocumentsApproved === true;
      case 2:
        // Step 2 validation: no validation required (Continue button always enabled)
        return true;
      case 3:
        // Step 3 validation: 2FA verified AND escrow checkbox checked
        return wizardState.step3Data?.verified === true && wizardState.step3Data?.escrowChecked === true;
      case 4:
        // Step 4: confirmation step, always valid
        return true;
      default:
        return false;
    }
  };

  // Prevent skipping steps - validate that user can access current step
  useEffect(() => {
    // Allow step 1 always
    if (currentStep === 1) return;

    // For steps 2-4, check if previous step is completed
    const previousStep = currentStep - 1;
    if (!validateStep(previousStep)) {
      // Redirect to first incomplete step
      navigate(`/buy/${productId}/step-${previousStep}`, { replace: true });
    }
  }, [currentStep, productId, navigate]);

  // Navigation handlers
  const handleNext = () => {
    if (currentStep < totalSteps && validateStep(currentStep)) {
      navigate(`/buy/${productId}/step-${currentStep + 1}`);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      navigate(`/buy/${productId}/step-${currentStep - 1}`);
    }
  };

  const handleCancel = () => {
    // Clear wizard state
    setWizardState({});
    // Navigate back to previous page or marketplace
    navigate(-1);
  };

  // Get canProceed status for current step
  const canProceed = validateStep(currentStep);

  // Memoize callbacks to prevent infinite loops
  const handleStep1StateChange = useCallback((data: { allDocumentsApproved: boolean; approvedCount: number }) => {
    setWizardState(prev => ({ ...prev, step1Data: data }));
  }, []);

  const handleStep2StateChange = useCallback((data: { termsReviewed: boolean }) => {
    setWizardState(prev => ({ ...prev, step2Data: data }));
  }, []);

  const handleStep3StateChange = useCallback((data: any) => {
    setWizardState(prev => ({ ...prev, step3Data: data }));
  }, []);

  // Render step content based on route
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return <BuyFlowStep1 productId={productId || ''} onStateChange={handleStep1StateChange} />;
      case 2:
        return <BuyFlowStep2 productId={productId || ''} onStateChange={handleStep2StateChange} />;
      case 3:
        return (
          <BuyFlowStep3Content 
            onStateChange={handleStep3StateChange} 
          />
        );
      case 4:
        return <div>Step 4: Confirmation (Placeholder for Story 5.5)</div>;
      default:
        return <div>Invalid step</div>;
    }
  };

  return (
    <WizardLayout
      currentStep={currentStep}
      totalSteps={totalSteps}
      onNext={handleNext}
      onBack={handleBack}
      onCancel={handleCancel}
      canProceed={canProceed}
    >
      {renderStepContent()}
    </WizardLayout>
  );
};

